import { Api } from "@/api/Api";
import { type TimeStateMachine, TimeStates } from "@/models/time-control/timeStateTypes";
import type { TimeState } from "@mm/metx-workbench.meteomatics.com";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import { DateTime, Duration } from "luxon";
import type { StateFrom } from "xstate";
import {
  type DeserializerMap,
  applyPropertyDeserializer,
  stringifyStateVal,
} from "../state-machine-utils/stateMachineUtils";
import {
  TIME_STATE_PERSIST_DEBOUNCE_MS,
  defaultTimeCtx,
  defaultTimeStateVal,
  timeStateSchemaVer,
} from "./timeStateConsts";
import type { AutoRefreshCtx, TimeContextUnion } from "./timeStateCtxTypes";

// ###########################################################################
//  Utilities to handle backend connections
// ###########################################################################

/**
 * A set of modes that should NOT be persisted after browser refresh.
 *
 * We use array because state value can be an object.
 */
const excludedModesList: Array<StateFrom<TimeStateMachine>["value"]> = [];

export function saveTimeStateDesc(
  profileId: number,
  state: StateFrom<TimeStateMachine>,
  excludedModes: Array<StateFrom<TimeStateMachine>["value"]> = excludedModesList,
): Promise<TimeState | null> {
  const shouldExclude = !!excludedModes.find((mode) => isEqual(mode, state.value));
  if (shouldExclude) {
    return Promise.resolve(null);
  }

  return Api.timeState.v1TimeStateUpdate({
    idProfile: profileId,
    timeStateUpdate: {
      schema_ver: timeStateSchemaVer,
      state_value: stringifyStateVal(state.value),
      context: state.context,
    },
  });
}

export const saveTimeStateDescDebounced = debounce(saveTimeStateDesc, TIME_STATE_PERSIST_DEBOUNCE_MS, {
  leading: false,
  trailing: true,
});

/**
 * Fetches a time state desc from backend and emits an event to the state machine.
 */
export function readTimeStateDesc(profileId: number) {
  return Api.timeState.v1TimeStateRead({ idProfile: profileId }).then((data) => {
    const [stateVal, ctx] = deserializeTimeStateDesc(data);
    return {
      stateValue: stateVal,
      context: ctx,
    };
  });
}

const realizeIsoDate = (isoStr: string) => DateTime.fromISO(isoStr, { setZone: true });
const realizeIsoDuration = (isoStr: string) => Duration.fromISO(isoStr);
const ctxFieldDeserializers: DeserializerMap = {
  displayTime: realizeIsoDate,
  nextDisplayTime_: realizeIsoDate,
  "modeOpts.startTime": realizeIsoDate,
  "modeOpts.endTime": realizeIsoDate,
  "modeOpts.temporalResolution": realizeIsoDuration,
  "modeOpts.animationTime_": realizeIsoDate,
  lastAutoRefreshTime: realizeIsoDate,
};

// Legacy state values that existed before the current AUTO_REFRESH / ANIMATION split.
// These do NOT relate to the current `isLocked` flag; they were historical state names.
const legacyStateValues = ["TIME_POINT.STORED_TIME", "StoredTimePoint"];

export const validTimeStates = new Set<string>([TimeStates.AUTO_REFRESH, TimeStates.ANIMATION]);

// ------------------------------------------------------------
// Schema migration helpers
// ------------------------------------------------------------
/**
 * Migrate a 1.0 time-state context to 1.1 without changing semantics.
 * - Normalizes required mode fields (`refreshRate`, `isLocked`).
 * - Ensures full AnimationModeOpts shape if we detect animation-ish fields.
 * - Leaves AUTO_REFRESH as a minimal, normalized shape.
 * - Drops invalid `lastAutoRefreshTime`.
 */
function migrateV1_0toV1_1(stateVal: string, ctx: TimeContextUnion): TimeContextUnion {
  const mo: any = (ctx as any).modeOpts ?? {};

  // 1) Required flags across modes
  const refreshRate = typeof mo.refreshRate === "number" ? mo.refreshRate : 0; // 0 => "no update"
  const isLocked = typeof mo.isLocked === "boolean" ? mo.isLocked : false;

  // 2) Detect if this looks like an Animation options object
  const looksLikeAnimation =
    mo && ("startTime" in mo || "endTime" in mo || "fps" in mo || "temporalResolution" in mo || "animationTime_" in mo);

  if (looksLikeAnimation) {
    const start = mo.startTime ?? (ctx as any).displayTime;
    const end = mo.endTime ?? start.plus({ days: 2 });
    const tr = mo.temporalResolution ?? Duration.fromISO("PT1H");
    const fps = typeof mo.fps === "number" ? mo.fps : 2;
    const isPaused = typeof mo.isPaused === "boolean" ? mo.isPaused : true;
    const isLooping = typeof mo.isLooping === "boolean" ? mo.isLooping : true;
    const animT = mo.animationTime_ ?? (ctx as any).displayTime;

    (ctx as any).modeOpts = {
      startTime: start,
      endTime: end,
      temporalResolution: tr,
      fps,
      isPaused,
      isLooping,
      animationTime_: animT,
      refreshRate,
      isLocked,
    };
  } else {
    // AUTO_REFRESH normalization
    (ctx as any).modeOpts = { refreshRate, isLocked };
  }

  // 3) Sanitize lastAutoRefreshTime (must be a valid Luxon DateTime or undefined)
  const last = (ctx as any).lastAutoRefreshTime;
  if (last !== undefined && !(last as any)?.isValid) {
    (ctx as any).lastAutoRefreshTime = undefined;
  }

  return ctx as TimeContextUnion;
}

/**
 * Deserialize JSON description of time state from backend into
 * instantiated a context and a state value object.
 */
export function deserializeTimeStateDesc(timeStateDesc: TimeState): [string, TimeContextUnion] {
  let context = applyPropertyDeserializer(timeStateDesc.context ?? {}, ctxFieldDeserializers) as TimeContextUnion;
  const stateVal: string = timeStateDesc.state_value;
  const schema_ver: string = timeStateDesc.schema_ver ?? "1.0"; // treat null/undefined as 1.0

  if (!(context as any).nextDisplayTime_) {
    (context as any).nextDisplayTime_ = (context as any).displayTime;
  }

  // Map legacy state values to AUTO_REFRESH with a sensible default
  if (legacyStateValues.includes(stateVal)) {
    const ctx: AutoRefreshCtx = {
      ...defaultTimeCtx,
      displayTime: (context as any).displayTime,
      nextDisplayTime_: (context as any).displayTime,
      timezone: (context as any).timezone,
      modeOpts: { refreshRate: 0, isLocked: true },
    };
    return [defaultTimeStateVal, ctx];
  }

  // Reject completely unknown state values early
  if (!validTimeStates.has(stateVal)) {
    return [defaultTimeStateVal, defaultTimeCtx];
  }

  // Perform schema migration when loading older payloads
  if (schema_ver === "1.0") {
    try {
      console.debug?.("[time-state] migrated 1.0 → 1.1", { stateVal });
    } catch {}
    context = migrateV1_0toV1_1(stateVal, context);
  } else if (schema_ver !== timeStateSchemaVer) {
    // Unknown future/older schema → fallback to fresh defaults to be safe
    return [defaultTimeStateVal, defaultTimeCtx];
  }

  // Normalize timezone to known enum values
  const tz = (context as any).timezone;
  if (tz !== "utc" && tz !== "local") {
    (context as any).timezone = defaultTimeCtx.timezone;
  }

  return [stateVal, context];
}
